import { useQuery } from "@tanstack/react-query";
import { getDataroomData } from "../model/services/getDataroomData";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { TableBody } from "./TableBody/TableBody";
import { TableLabels } from "./TableLabels";
import { DataroomFileContainer } from "./DataroomFileContainer/DataroomFileContainer";
import {
  DataroomFileProgressModal,
  type TDataroomModalHandles
} from "./DataroomFileProgressModal/DataroomFileProgressModal";

import styles from "./DataRoomSection.module.scss";
import { useRef } from "react";
import { Typography } from "@/frontend/shared/ui/Typography";
import { EAccessDataStatus } from "@/frontend/features/Company/model/types/types";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";

type TDataRoomSectionProps = {
  id: number;
  dataStatusFromAccess?: EAccessDataStatus;
  companyDataId?: number;
  triggerRefetchAccess: () => void;
  isLoadingRequestAccess: boolean;
  setIsLoadingRequestAccess: (value: boolean) => void;
  isDataRoomPending?: boolean;
};

export type TSectionOption = {
  section: string;
  filename: string;
  id: number;
};

export function DataRoomSection({
  id,
  dataStatusFromAccess,
  companyDataId,
  triggerRefetchAccess,
  isLoadingRequestAccess,
  setIsLoadingRequestAccess,
  isDataRoomPending
}: TDataRoomSectionProps) {
  const modalRef = useRef<TDataroomModalHandles>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["dataRoomData"],
    queryFn: async () => await getDataroomData({ id }),
    gcTime: 0,
    refetchOnWindowFocus: false,
    throwOnError: (error) => {
      console.error(error);
      reportErrorToSentry(error, "PORTFOLIO COMPANY PROFILE: //portfolio-companies/${id}/data-room", "getDataroomData");
      return true;
    }
  });

  const hasData = !!data && data?.length > 0;

  const emptyPageContent = (
    <div style={{ overflow: "hidden" }}>
      <TableLabels />

      <div className={styles.emptyPageContainer}>
        <div className={styles.emptyPageContent}>
          <Typography variant="heading-7" color="greyscale800">
            Add files
          </Typography>
          <Typography variant="body-2" color="greyscale800">
            use "File Upload" button
          </Typography>
        </div>
      </div>
    </div>
  );

  const content = isLoading ? (
    <div className={styles.loadingContainer}>
      <Loader />
    </div>
  ) : (
    <div className={styles.tableContainer}>
      <TableLabels />
      <TableBody
        data={data || []}
        triggerRefetch={refetch}
        companyId={id}
        dataStatusFromAccess={dataStatusFromAccess}
        companyDataId={companyDataId}
        triggerRefetchAccess={triggerRefetchAccess}
        isLoadingRequestAccess={isLoadingRequestAccess}
        setIsLoadingRequestAccess={setIsLoadingRequestAccess}
        isDataRoomPending={isDataRoomPending}
      />
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.categoriesContainer}>
        <DataroomFileProgressModal ref={modalRef} />
        <DataroomFileContainer
          name="Other"
          companyId={id}
          triggerRefetch={refetch}
          openModal={() => modalRef?.current?.openModal()}
        >
          {hasData ? content : emptyPageContent}
        </DataroomFileContainer>
      </div>
    </div>
  );
}
